import { router } from "@inertiajs/react"
import { CheckCircle2, Inbox } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import { SubmissionReviewCard } from "@/components/submissions/submission-review-card"
import { useCurrentContext } from "@/hooks/use-current-context"
import type { SubmissionsToReviewSection as SectionData } from "@/types/home-triage"

/**
 * OKS-708 — Agency "Submissions to review" section: the agency-home
 * consumer of the shipped {@link HomeTriageSection} shell +
 * {@link SubmissionReviewCard}. Sibling of the candidates section, mounted
 * directly below it (mirrors the brand-home order).
 *
 * Agency deltas vs the OKS-707 brand consumer: reloads the AGENCY prop key
 * `agency_submissions_to_review` on Retry; cold-start / empty copy naming
 * the SELECTED brand (OKS-854 — was portfolio-level back when home
 * aggregated every assigned brand); per-card `brand` badge (auto-rendered
 * from the item payload). The reused Approve/Flag dialogs POST to the
 * agency `/agency/submissions/*` endpoints via the server-supplied action
 * URLs (URL-nullability).
 */
export interface AgencySubmissionsToReviewSectionProps {
  section: SectionData
  /** OKS-923 — see the brand twin; set by the `<Deferred>` fallback. */
  isRefreshing?: boolean
  /** OKS-923 — refresh window expired; forces the shell's existing error state. */
  refreshFailed?: boolean
}

export function AgencySubmissionsToReviewSection({
  section,
  isRefreshing = false,
  refreshFailed = false,
}: AgencySubmissionsToReviewSectionProps) {
  const { t } = useTranslation()
  const { currentBrand, availableBrands } = useCurrentContext()
  const [isRetrying, setIsRetrying] = useState(false)

  // OKS-854 — the copy names the selected brand (UX Spec §3). The
  // switcher hint only appears when there is somewhere to switch TO:
  // the switcher is hidden entirely for single-brand agencies, so
  // pointing at a control that isn't on screen would be worse than
  // saying nothing.
  const brandName = currentBrand?.name ?? ""
  const showSwitcherHint = availableBrands.length > 1

  function handleRetry() {
    setIsRetrying(true)
    router.reload({
      only: ["agency_submissions_to_review"],
      onFinish: () => setIsRetrying(false),
    })
  }

  const state = isRetrying ? "loading" : refreshFailed ? "error" : section.state

  return (
    <HomeTriageSection
      title={t("submissions.home_review.title")}
      state={state}
      count={section.count}
      viewAllUrl={section.view_all_url}
      onRetry={handleRetry}
      isRetrying={isRetrying}
      isRefreshing={isRefreshing}
      testId="agency-submissions-to-review"
      renderColdStart={() => (
        <EmptyState
          icon={Inbox}
          title={t("agency_home.submissions.cold_start.title")}
          titleAs="h2"
          description={t("agency_home.submissions.cold_start.description", {
            brand: brandName,
          })}
          data-testid="agency-submissions-to-review-cold-start"
        />
      )}
      renderEmpty={() => (
        <EmptyState
          icon={CheckCircle2}
          title={t("agency_home.submissions.empty.title")}
          titleAs="h2"
          description={[
            t("agency_home.submissions.empty.description", {
              brand: brandName,
            }),
            showSwitcherHint
              ? t("agency_home.submissions.empty.switcher_hint")
              : null,
          ]
            .filter(Boolean)
            .join(" ")}
          data-testid="agency-submissions-to-review-empty"
        />
      )}
    >
      {section.items.map((item) => (
        <SubmissionReviewCard key={item.id} item={item} />
      ))}
    </HomeTriageSection>
  )
}
